import { useEffect, useState } from "react";
import {
  ArrowRight,
  CheckCircle2,
  Clock3,
  Headphones,
  Mail,
  MapPin,
  HelpCircle,
  MessageSquareText,
  Send,
  ShieldCheck,
  Sparkles,
  UsersRound,
} from "lucide-react";
import SiteHeader from "./SiteHeader";
import SiteFooter from "./SiteFooter";
import Reveal from "./Reveal";
import Breadcrumbs from "./Breadcrumbs";

const CONTACT_EMAIL = import.meta.env.VITE_CONTACT_EMAIL || "hello@cullath.com , sales@cullath.com";

const contactOptions = [
  {
    icon: UsersRound,
    title: "Talk to Sales",
    copy: "Explore Cullath for your team, office, or growing organization.",
    topic: "Sales inquiry",
  },
  {
    icon: Headphones,
    title: "Product & Technical Support",
    copy:  <>"Get help with{" "}
      <a href="https://cullath.com/connect" className="highlight-link-contack-page">
        Connect
      </a> ,  <a href="https://cullath.com/connect" className="highlight-link-contack-page">
        Desk
      </a> , <a href="https://cullath.com/connect" className="highlight-link-contack-page">
        Assist
      </a>, account access, setup, or technical questions.",
    topic: "Product support",</>
  },
  {
    icon: HelpCircle,
    title: "General Questions",
    copy: "Ask about Cullath, our products, pricing, partnerships, or anything else",
    topic: "General question",
  },
];

export default function ContactPage() {
  const [topic, setTopic] = useState("Sales inquiry");
  const [status, setStatus] = useState("");
  
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);
  useEffect(() => {
  const canonicalUrl = "https://cullath.com/contact-us";

  let canonical = document.querySelector('link[rel="canonical"]');

  if (!canonical) {
    canonical = document.createElement("link");
    canonical.setAttribute("rel", "canonical");
    document.head.appendChild(canonical);
  }

  canonical.setAttribute("href", canonicalUrl);

  return () => {
    canonical.remove();
  };
}, []);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
   document.title = "Contact Cullath | Sales, Support & Demo Requests";

  const description =
    "Contact Cullath for sales inquiries, demo requests, product support, or general questions about Connect, Desk, and Assist. Our team is ready to help.";

    const metaDescription = document.querySelector(
    'meta[name="description"]'
  );

  if (metaDescription) {
    metaDescription.setAttribute("content", description);
  } else {
    const meta = document.createElement("meta");
    meta.name = "description";
    meta.content = description;
    document.head.appendChild(meta);
  }

  const schemas = [
   {
  "@context": "https://schema.org",
  "@type": "ContactPage",
  "@id": "https://cullath.com/contact-us/#contactpage",
  "url": "https://cullath.com/contact-us",
  "name": "Contact Cullath | Sales, Support & Demo Requests",
  "description": "Contact Cullath for sales inquiries, demo requests, product support, or general questions about Connect, Desk, and Assist. Our team is ready to help.",
  "isPartOf": {
    "@id": "https://cullath.com/#website"
  },
  "about": {
    "@id": "https://cullath.com/#organization"
  },
  "inLanguage": "en-US"
},
   {
  "@context": "https://schema.org",
  "@type": "ContactPage",
  "@id": "https://cullath.com/contact-us/#contactpage",
  "url": "https://cullath.com/contact-us",
  "name": "Contact Cullath | Sales, Support & Demo Requests",
  "description": "Contact Cullath for sales inquiries, demo requests, product support, or general questions about Connect, Desk, and Assist. Our team is ready to help.",
  "isPartOf": {
    "@id": "https://cullath.com/#website"
  },
  "about": {
    "@id": "https://cullath.com/#organization"
  },
  "inLanguage": "en-US"
}
  ];

  const schemaElements = schemas.map((schema, index) => {
    const script = document.createElement("script");

    script.type = "application/ld+json";
    script.setAttribute(
      "data-cullath-connect-schema",
      String(index)
    );
    script.textContent = JSON.stringify(schema);

    document.head.appendChild(script);

    return script;
  });

  return () => {
    schemaElements.forEach((script) => script.remove());
  };
  }, []);

  const selectTopic = (nextTopic) => {
    setTopic(nextTopic);
    window.requestAnimationFrame(() => {
      document.getElementById("contact-form")?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSubmitting) return;

    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    const payload = {
      name: String(form.get("name") || "").trim(),
      email: String(form.get("email") || "").trim(),
      company: String(form.get("company") || "").trim(),
      topic: String(form.get("topic") || topic).trim(),
      message: String(form.get("message") || "").trim(),
    };

    setIsSubmitting(true);
    setStatus("Sending your message...");

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(result.message || "Unable to send your message right now.");
      }

      setStatus("Your message has been sent successfully. We will contact you soon.");
      formElement.reset();
      setTopic("Sales inquiry");
    } catch (error) {
      setStatus(error.message || "Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-page-shell">
      <SiteHeader isProductPage isHeroPage />

      <main className="contact-page">
        <section className="contact-hero">
          <div className="contact-hero-grid" aria-hidden="true" />
          <div className="contact-orbit contact-orbit--one" aria-hidden="true" />
          <div className="contact-orbit contact-orbit--two" aria-hidden="true" />
          <div className="contact-orbit contact-orbit--three" aria-hidden="true" />
          <span className="contact-node contact-node--one" aria-hidden="true" />
          <span className="contact-node contact-node--two" aria-hidden="true" />
          <span className="contact-node contact-node--three" aria-hidden="true" />

          <div className="contact-container contact-hero-layout">
            <Reveal className="contact-hero-copy">
              <Breadcrumbs items={[{ label: "Home", href: "/" }, { label: "Contact Us" }]} />
              <span className="contact-kicker"><Sparkles size={15} /> LET&apos;S CONNECT</span>
              <h1>Contact Cullath. Let's <em>Build Better Work Together.</em></h1>
              <p>
               Whether you're exploring Cullath, requesting a demo, contacting sales, or have questions about  <a href="https://cullath.com/"
                  className="highlight-link-contack-page">
                  Connect
                </a>,  <a href="https://cullath.com/"
                  className="highlight-link-contack-page">
                 Desk
                </a>, or <a href="https://cullath.com/"
                  className="highlight-link-contack-page">
                  Assist
                </a> , our team is here to help you find the right solution for your business.
              </p>
              <div className="contact-hero-points">
                <span><CheckCircle2 size={17} /> Clear next steps for your request</span>
                <span><Clock3 size={17} /> Fast, focused response</span>
                <span><ShieldCheck size={17} /> Your information stays private</span>
              </div>
            </Reveal>

            <Reveal className="contact-hero-card" delay={120}>
              <div className="contact-hero-card__top">
                <span><MessageSquareText size={20} /></span>
                <div>
                  <small>CULLATH CONTACT</small>
                  <strong>Start the right conversation.</strong>
                </div>
              </div>
              <div className="contact-route-list">
                {contactOptions.map((option, index) => {
                  const Icon = option.icon;
                  return (
                    <button type="button" onClick={() => selectTopic(option.topic)} key={option.title}>
                      <span><Icon size={19} /></span>
                      <div><strong>{option.title}</strong><small>{option.copy}</small></div>
                      <ArrowRight size={17} />
                      <i>{String(index + 1).padStart(2, "0")}</i>
                    </button>
                  );
                })}
              </div>
            </Reveal>
          </div>
        </section>

        <section className="contact-content" id="contact-form">
          <div className="contact-container contact-content-grid">
            <Reveal className="contact-info-panel">
              <span className="contact-section-kicker">CONTACT CULLATH</span>
              <h2>One Message.<em>The Right Cullath Team.</em></h2>
              <p>
             Choose the reason for contacting Cullath and share the details that matter most. Your message will be securely delivered to the right team for a faster response.
              </p>

              <div className="contact-info-cards">
                <article>
                  <span><Mail size={19} /></span>
                  <div><small>EMAIL</small><a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a></div>
                </article>
                <article>
                  <span><Clock3 size={19} /></span>
                  <div><small>RESPONSE WINDOW</small><strong>Usually within one business day</strong></div>
                </article>
                <article>
                  <span><MapPin size={19} /></span>
                  <div><small>WORKING WITH</small><strong>Teams and businesses worldwide</strong></div>
                </article>
              </div>

              <div className="contact-note">
                <Sparkles size={18} />
                <p><strong>Helpful detail:</strong>Include your organization size and whether you're interested in Connect, Desk, or Assist so we can direct your inquiry faster.</p>
              </div>
            </Reveal>

            <Reveal className="contact-form-card" delay={100}>
              <div className="contact-form-heading">
                <span><Send size={18} /></span>
                <div>
                  <small>SEND A MESSAGE</small>
                  <h3>How can we help?</h3>
                </div>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="contact-form-row">
                  <label>
                    <span>Your name</span>
                    <input name="name" type="text" placeholder="Enter your name" required />
                  </label>
                  <label>
                    <span>Work email</span>
                    <input name="email" type="email" placeholder="you@company.com" required />
                  </label>
                </div>

                <div className="contact-form-row">
                  <label>
                    <span>Company</span>
                    <input name="company" type="text" placeholder="Company or organization" />
                  </label>
                  <label>
                    <span>How can we help?</span>
                    <select name="topic" value={topic} onChange={(event) => setTopic(event.target.value)}>
                      <option>Sales inquiry</option>
                      <option>Product support</option>
                      <option>Product feedback</option>
                      <option>General question</option>
                    </select>
                  </label>
                </div>

                <label className="contact-message-field">
                  <span>Message</span>
                  <textarea name="message" rows="6" placeholder="Tell us about your team, goal, or question..." required />
                </label>

                <div className="contact-form-bottom">
                  <p><ShieldCheck size={16} /> Your details are used only to respond to this request.</p>
                  <button type="submit" disabled={isSubmitting}>{isSubmitting ? "Sending..." : "Send Message"} <Send size={17} /></button>
                </div>
                {status && <p className="contact-form-status" role="status">{status}</p>}
              </form>
            </Reveal>
          </div>
        </section>

        <section className="contact-bottom-cta">
          <div className="contact-container">
            <Reveal>
              <span><img src="/1.png" alt="" /></span>
              <div>
                <small>WHERE WORK ORBITS.</small>
                <h2>Ready to Explore the Complete Cullath Workplace Platform?</h2>
              </div>
              <a href="/#product">Explore Cullath <ArrowRight size={18} /></a>
            </Reveal>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
